import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, CalendarClock, Layers, Briefcase, FileText, FileEdit, Scale, Mail, Users, Settings, LogOut, ExternalLink } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, testid: "admin-nav-enquiries" },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarClock, testid: "admin-nav-bookings" },
  { to: "/admin/services", label: "Services", icon: Layers, testid: "admin-nav-services" },
  { to: "/admin/portfolio", label: "Portfolio", icon: Briefcase, testid: "admin-nav-portfolio" },
  { to: "/admin/posts", label: "Articles", icon: FileText, testid: "admin-nav-posts" },
  { to: "/admin/content", label: "Content", icon: FileEdit, testid: "admin-nav-content" },
  { to: "/admin/legal", label: "Legal", icon: Scale, testid: "admin-nav-legal" },
  { to: "/admin/templates", label: "Emails", icon: Mail, testid: "admin-nav-templates" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login", { replace: true, state: { from: location.pathname } }))
      .finally(() => setChecking(false));
  }, [navigate, location.pathname]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-black" data-testid="admin-auth-checking">
        <p className="text-sm text-brand-mist">Checking access…</p>
      </div>
    );
  }
  if (!user) return null;

  // Editors see content tools only; admin-only modules (enquiries, settings,
  // users, legal, templates, bookings, overview stats) are enforced server-side.
  const visibleNav = user.role === "admin" ? NAV : NAV.filter((n) => ["portfolio", "posts", "content"].some((k) => n.to.endsWith(k)));

  return (
    <div className="flex min-h-screen bg-brand-black" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 flex w-16 flex-col border-r border-brand-mist/10 bg-brand-charcoal lg:w-60">
        <div className="flex h-16 items-center justify-center border-b border-brand-mist/10 px-4 lg:justify-start">
          <Logo compact />
          <span className="ml-2.5 hidden font-display text-sm font-semibold text-brand-snow lg:inline">Admin</span>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Admin">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive ? "bg-brand-emerald/10 text-brand-emerald" : "text-brand-mist hover:bg-white/5 hover:text-brand-snow"
                )
              }
            >
              <item.icon size={17} className="shrink-0" />
              <span className="hidden lg:inline">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-brand-mist/10 p-3">
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-brand-mist transition-colors hover:bg-white/5 hover:text-brand-snow">
            <ExternalLink size={17} className="shrink-0" />
            <span className="hidden lg:inline">View website</span>
          </a>
          <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-brand-mist transition-colors hover:bg-white/5 hover:text-red-400">
            <LogOut size={17} className="shrink-0" />
            <span className="hidden lg:inline">Sign out</span>
          </button>
        </div>
      </aside>
      <main className="ml-16 flex-1 p-5 sm:p-8 lg:ml-60 lg:p-10">
        <Outlet context={{ user }} />
      </main>
    </div>
  );
};

export default AdminLayout;
